import { useTokenizedApi } from '../useTokenizedApi';
import { useQuery } from 'react-query';
import { Instrument, TInstrumentDescription } from '@tokenized/sdk-js-private';
import { usePersonalProfile } from '../entities/usePersonalProfile';
import { useProfile } from '../entities/useProfile';
import { useIntl } from '../intl/useIntl';
import { useStaticResources } from '../staticResources/useStaticResources';

/** @category Instruments */
export function useInstrumentWithDetails(
  instrumentDescription: TInstrumentDescription,
  profileId?: string,
) {
  const tokenizedApi = useTokenizedApi();
  const intl = useIntl();
  const personalProfile = usePersonalProfile();
  profileId = profileId || personalProfile?.id;
  const displayCurrencyCode = useProfile(profileId)?.displayCurrency;
  const currencies = useStaticResources()?.currencies;

  let instrument = new Instrument(instrumentDescription)
    .withIntl(intl)
    .withCurrenciesStaticResources(currencies);

  // This allows fully-functional Instrument objects to be constructed from
  // contract instrument draft IDs (the instrument details are extracted
  // from contract draft data in the query cache)
  instrument =
    tokenizedApi.contracts.setInstrumentDetailsFromContractDraft(instrument);

  const instrumentDetails = useQuery(
    tokenizedApi.treasury.instrumentDetailsQuery(
      instrument.instrumentIdToQuery,
    ),
  )?.data;
  instrument = instrument.withInstrumentDetails(instrumentDetails);

  const exchangeRates = useQuery(
    tokenizedApi.treasury.allExchangeRatesQuery(),
  ).data;

  instrument = instrument.withDisplayCurrencyRate(
    displayCurrencyCode,
    exchangeRates?.findRate(
      instrument.exchangeRateToQuery,
      displayCurrencyCode,
    ),
  );

  return instrument;
}
